<template>
  <div class="user-bind-devices-container">
    <el-form ref="addForm" :model="addForm" :rules="addFormRules" label-width="120px" class="add-device-form">
      <el-form-item :label="productType === 'DEVICE' ? '设备编码' : 'NFC编码'" prop="deviceCode">
        <div class="device-code-input">
          <el-input 
            v-model="addForm.deviceCode" 
            :placeholder="productType === 'DEVICE' ? '请输入设备编码(UUID)' : '请输入NFC卡编码'"
            clearable
            @keyup.enter.native="handleQueryProfile"
          ></el-input>
          <el-button type="primary" @click="handleQueryProfile" :loading="queryProfileLoading">
            获取信息
          </el-button>
        </div>
      </el-form-item>

      <div v-if="profileInfo" class="profile-info-card">
        <el-descriptions :column="2" border size="medium">
          <el-descriptions-item label="设备名称">
            {{ profileInfo.deviceName || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="产品类型">
            <el-tag :type="profileInfo.productType === 'DEVICE' ? 'primary' : 'success'" size="small">
              {{ profileInfo.productType === 'DEVICE' ? '智能设备' : '周边设备' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="产品分类" v-if="profileInfo.category">
            {{ profileInfo.category }}
          </el-descriptions-item>
          <el-descriptions-item label="SKU" v-if="profileInfo.sku">
            {{ profileInfo.sku }}
          </el-descriptions-item>
          <el-descriptions-item label="设备型号" v-if="profileInfo.device && (profileInfo.device.boardLabel || profileInfo.device.board)">
            {{ profileInfo.device.boardLabel || profileInfo.device.board }}
          </el-descriptions-item>
          <el-descriptions-item label="固件版本" v-if="profileInfo.device && profileInfo.device.appVersion">
            {{ profileInfo.device.appVersion }}
          </el-descriptions-item>
        </el-descriptions>

        <el-alert
          v-if="profileInfo.deviceExists && profileInfo.isBound"
          title="该设备已被其他用户绑定，无法重复绑定"
          type="error"
          :closable="false"
          show-icon
          style="margin-top: 12px;"
        ></el-alert>

        <el-alert
          v-else-if="!profileInfo.exists && !profileInfo.deviceExists"
          title="未找到该编码对应的产品信息，请确认编码是否正确"
          type="warning"
          :closable="false"
          show-icon
          style="margin-top: 12px;"
        ></el-alert>

        <el-alert
          v-else
          title="产品信息获取成功，请选择关联项后确认绑定"
          type="success"
          :closable="false"
          show-icon
          style="margin-top: 12px;"
        ></el-alert>
      </div>

      <el-form-item label="绑定智能体" prop="agentId" v-if="productType === 'DEVICE'">
        <el-select
          v-model="addForm.agentId"
          filterable
          clearable
          remote
          :remote-method="handleAgentSearch"
          :loading="agentLoading"
          placeholder="请选择智能体（必填）"
          style="width: 100%"
          no-data-text="无匹配智能体"
        >
          <el-option
            v-for="agent in agentOptions"
            :key="agent.id"
            :label="agent.agentName"
            :value="agent.id"
          >
            <span>{{ agent.agentName }}</span>
            <span style="float: right; color: #8492a6; font-size: 12px;">{{ agent.agentCode || '' }}</span>
          </el-option>
        </el-select>
        <span class="form-tip">仅显示当前用户拥有的智能体</span>
      </el-form-item>
    </el-form>

    <div class="user-bind-devices-actions">
      <el-button @click="handleCancel">
        {{ $t('common.cancel') }}
      </el-button>
      <el-button 
        type="primary" 
        @click="handleConfirmAdd" 
        :disabled="!canConfirm"
        :loading="addLoading"
        v-if="hasPermission('sys:device:update')"
      >
        确认绑定
      </el-button>
    </div>
  </div>
</template>

<script>
import { getProfileByDeviceCode, bindDeviceByCode } from '@/apis/device/device'
import { hasPermission } from '@/utils/permission'
import request from '@/utils/request'

export default {
  name: 'UserBindDevices',
  props: {
    userId: {
      type: [String, Number],
      required: true
    },
    excludeDeviceIds: {
      type: Array,
      default: () => []
    },
    productType: {
      type: String,
      default: 'DEVICE'
    }
  },
  data() {
    return {
      addForm: {
        deviceCode: '',
        deviceName: '',
        agentId: ''
      },
      addFormRules: {
        deviceCode: [
          { required: true, message: this._getDeviceCodePlaceholder, trigger: 'blur' }
        ],
        agentId: [
          { required: true, message: '请选择绑定的智能体', trigger: 'change' }
        ]
      },
      profileInfo: null,
      queryProfileLoading: false,
      addLoading: false,
      agentOptions: [],
      agentLoading: false
    }
  },
  computed: {
    _getDeviceCodePlaceholder() {
      return this.productType === 'DEVICE' ? '请输入设备编码' : '请输入NFC编码'
    },
    canConfirm() {
      if (!this.addForm.deviceCode || !this.addForm.deviceCode.trim()) return false
      if (this.productType === 'DEVICE' && !this.addForm.agentId) return false
      if (!this.profileInfo) return false
      if (this.profileInfo.deviceExists && this.profileInfo.isBound) return false
      if (!this.profileInfo.exists && !this.profileInfo.deviceExists) return false
      return true
    }
  },
  mounted() {
    if (this.productType === 'DEVICE') {
      this.fetchAgentOptions()
    }
  },
  methods: {
    hasPermission,

    async fetchAgentOptions() {
      this.agentLoading = true
      try {
        const res = await request({
          url: '/agent/all',
          method: 'get',
          params: {
            userId: this.userId,
            limit: 1000
          }
        })
        this.agentOptions = res.list || []
      } catch (error) {
        console.error('Failed to load agent options:', error)
        try {
          const res = await request({
            url: '/agent/list',
            method: 'get',
            params: { userId: this.userId }
          })
          this.agentOptions = Array.isArray(res) ? res : (res.list || [])
        } catch (err) {
          console.error('Failed to load agent options (fallback):', err)
          this.agentOptions = []
        }
      } finally {
        this.agentLoading = false
      }
    },

    handleAgentSearch(query) {
      if (query) {
        this.agentOptions = this.agentOptions.filter(agent =>
          agent.agentName && agent.agentName.toLowerCase().includes(query.toLowerCase())
        )
      } else {
        this.fetchAgentOptions()
      }
    },

    async handleQueryProfile() {
      if (!this.addForm.deviceCode || !this.addForm.deviceCode.trim()) {
        this.$message.warning(this.productType === 'DEVICE' ? '请输入设备编码' : '请输入NFC编码')
        return
      }
      this.queryProfileLoading = true
      this.profileInfo = null
      try {
        const data = await getProfileByDeviceCode(this.addForm.deviceCode.trim())
        if (data) {
          this.profileInfo = data
        } else {
          this.$message.warning('未找到该编码对应的产品信息')
        }
      } catch (error) {
        console.error('Query profile error:', error)
        this.$message.error('查询产品信息失败')
      } finally {
        this.queryProfileLoading = false
      }
    },

    handleCancel() {
      this.$emit('close')
    },

    async handleConfirmAdd() {
      if (!this.addForm.deviceCode || !this.addForm.deviceCode.trim()) {
        this.$message.warning(this.productType === 'DEVICE' ? '请输入设备编码' : '请输入NFC编码')
        return
      }
      if (this.productType === 'DEVICE' && !this.addForm.agentId) {
        this.$message.warning('请选择绑定的智能体')
        return
      }
      if (!this.profileInfo) {
        this.$message.warning('请先点击"获取信息"查询产品信息')
        return
      }
      if (!this.profileInfo.exists && !this.profileInfo.deviceExists) {
        this.$message.warning('未找到该编码对应的产品信息，无法绑定')
        return
      }
      if (this.profileInfo.deviceExists && this.profileInfo.isBound) {
        this.$message.warning('该设备已被其他用户绑定，无法重复绑定')
        return
      }

      this.addLoading = true
      try {
        const data = {
          deviceCode: this.addForm.deviceCode.trim(),
          userId: this.userId
        }
        if (this.productType === 'DEVICE') {
          data.agentId = this.addForm.agentId
        }
        await bindDeviceByCode(data)
        this.$message.success(this.productType === 'DEVICE' ? '设备绑定成功' : 'NFC卡绑定成功')
        this.$emit('success')
      } catch (error) {
        console.error('Bind by code error:', error)
        const msg = (error.response && error.response.data && error.response.data.msg) || '绑定失败'
        this.$message.error(msg)
      } finally {
        this.addLoading = false
      }
    }
  }
}
</script>

<style scoped>
.user-bind-devices-container {
  margin-top: -15px;
  padding: 0;
}

.add-device-form {
  padding: 10px 20px 0;
}

.device-code-input {
  display: flex;
  align-items: center;
}

.profile-info-card {
  margin-bottom: 16px;
}

.user-bind-devices-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-left: 8px;
}
</style>
